/**
 * ComplianceQueueRow — shared row style for RCC's 14 surfaces.
 *
 * One atomic queue-item row with:
 *  - severity stripe (info / warn / err based on age/status)
 *  - primary title + sub-label
 *  - optional age/SLA pill on the right
 *  - click handler for open-detail navigation
 *
 * Lives inside RCC because no other module reuses this pattern yet. Promote to
 * shared primitives if RAS / RKL adopt it later.
 */

import type { ReactNode } from 'react';
import { Pill } from '@/components/primitives';

export type QueueRowSeverity = 'ok' | 'info' | 'warn' | 'err';

export interface ComplianceQueueRowProps {
  title: string;
  subtitle?: string;
  meta?: string;
  severity?: QueueRowSeverity;
  slaBadge?: { variant: 'ok' | 'warn' | 'err' | 'info' | 'muted'; label: string };
  icon?: ReactNode;
  onClick?: () => void;
  actions?: ReactNode;
}

export function ComplianceQueueRow({
  title,
  subtitle,
  meta,
  severity = 'info',
  slaBadge,
  icon,
  onClick,
  actions,
}: ComplianceQueueRowProps): JSX.Element {
  const stripeColor =
    severity === 'err'
      ? 'var(--pcb-red)'
      : severity === 'warn'
        ? 'var(--pcb-amber-text)'
        : severity === 'ok'
          ? 'var(--pcb-green)'
          : 'var(--pcb-blue)';

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      style={{
        display: 'grid',
        gridTemplateColumns: '4px 28px 1fr auto auto',
        alignItems: 'center',
        gap: 'var(--sp-5)',
        padding: 'var(--sp-5) var(--sp-6)',
        background: 'var(--surface-card)',
        borderLeft: `4px solid ${stripeColor}`,
        borderRadius: 'var(--r-md)',
        boxShadow: 'var(--sh-1)',
        marginBottom: 'var(--sp-4)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <span aria-hidden />
      <span
        aria-hidden
        style={{
          width: 28,
          height: 28,
          borderRadius: 'var(--r-full)',
          background: 'var(--ink-100)',
          display: 'grid',
          placeItems: 'center',
          color: 'var(--pcb-blue-dark)',
          fontSize: 12,
        }}
      >
        {icon ?? '!'}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink-900)' }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 2 }}>{subtitle}</div>
        )}
        {meta && (
          <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 2 }}>{meta}</div>
        )}
      </div>
      {slaBadge ? <Pill variant={slaBadge.variant}>{slaBadge.label}</Pill> : <span />}
      <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>{actions}</div>
    </div>
  );
}
